import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Renders `sections/recently-viewed.liquid`.
 *
 * The product history lives in localStorage, so the server cannot know it. This
 * element re-requests its own section through the Section Rendering API against a
 * search URL filtered to the remembered ids -- the same technique
 * `assets/predictive-search.js` already uses for this list, reused deliberately
 * rather than inventing a second mechanism.
 *
 * The section ships `hidden`. It only ever reveals itself once at least one card
 * has actually rendered, so a first-time visitor sees nothing instead of an empty
 * heading, and a failed fetch leaves no trace.
 */
class RecentlyViewedSection extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  async connectedCallback() {
    const grid = this.querySelector('.recently-viewed__grid');
    if (!grid) return;

    let ids = RecentlyViewed.getProducts();

    // On a product page, the product being looked at is not "recently viewed".
    const exclude = this.dataset.exclude;
    if (exclude) ids = ids.filter((id) => String(id) !== String(exclude));

    const limit = Number(this.dataset.limit) || 4;
    ids = ids.slice(0, limit);

    if (ids.length === 0) return;

    try {
      this.#controller = new AbortController();

      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');

      const html = await sectionRenderer.getSectionHTML(
        this.dataset.sectionId ?? this.getAttribute('id') ?? '',
        false,
        url,
        this.#controller.signal
      );
      if (!html) return;

      const rendered = new DOMParser().parseFromString(html, 'text/html').querySelector('.recently-viewed__grid');
      if (!rendered || rendered.children.length === 0) return;

      grid.replaceChildren(...rendered.children);
      this.hidden = false;
    } catch {
      // A failed or aborted fetch simply leaves the section hidden. There is no
      // useful fallback for "products this visitor looked at" and an empty,
      // headed section would be worse than none.
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewedSection);
}
